import { renderOfferingBadge } from "./offering-badges";
import { html, nothing, svg, type TemplateResult } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import type { ScheduleOptions } from "../../config/types";
import type { MockClass } from "../data/mock-schedule";
import { mapsHref, preferredLocationQuery } from "../schedule-helpers";
import { formatMinorAmount } from "../format-money";

const ICON_CARET = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`;

export interface ScheduleRowViewModel {
  options: ScheduleOptions;
  isPast: boolean;
  registrationClosed?: boolean;
  registrationNotYetOpen?: boolean;
  capacity: { text: string; waiting: boolean } | null;
  displayTime: string;
  timezoneAbbreviation: string;
  expanded: boolean;
  bioExpanded: boolean;
  descriptionLoading: boolean;
  descriptionText: string | undefined;
  onToggleDescription: () => void;
  onToggleBio: () => void;
  onBook: () => void;
  onBuyout?: () => void;
}

export function renderScheduleRow(
  occurrence: MockClass,
  model: ScheduleRowViewModel,
): TemplateResult {
  const options = model.options;
  const noThumbnail = options.hideThumbnail || options.thumbnail === "none";
  const rowClass = `row ${options.rowStyle === "cards" ? "cards" : "lines"} ${
    noThumbnail ? "no-thumb" : ""
  } ${model.isPast ? "past" : ""}`;
  const secondaryTime = [
    options.hideDuration ? "" : occurrence.duration,
    model.timezoneAbbreviation,
  ]
    .filter(Boolean)
    .join(" · ");
  const initials = (
    options.thumbnail === "class" ? occurrence.name : occurrence.instructor
  )
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("");
  const external = !!occurrence.externalTickets && !!occurrence.externalTicketUrl;
  const isWaitlist = !external && occurrence.capacityState === "waitlist";
  const notYetOpen = !external && model.registrationNotYetOpen === true;
  const label = notYetOpen ? "Not yet open" :
    options.buttonText ??
    (occurrence.offeringKind === "program"
      ? "View program"
      : isWaitlist
        ? "Join waitlist"
        : "Book class");
  const shortLabel = notYetOpen ? "Not yet open" : options.buttonText
    ? options.buttonText.split(" ")[0]
    : occurrence.offeringKind === "program"
      ? "View"
      : isWaitlist
        ? "Waitlist"
        : "Book";
  const thumbnail =
    options.thumbnail === "class" ? occurrence.classPhoto : occurrence.photo;
  const hasDescription =
    !!occurrence.description || !!occurrence.hasDescription;
  const hasBio = !!occurrence.hasInstructorBio && !!occurrence.instructorBio;
  const className = [
    "cname",
    occurrence.cancelled ? "cancelled" : "",
    hasDescription ? "expandable" : "",
    model.expanded ? "expanded" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return html`
    <div class=${rowClass} data-occurrence-id=${occurrence.id}>
      <div>
        <div class="time ${occurrence.cancelled ? "cancelled" : ""}">
          ${model.displayTime}
        </div>
        ${
          secondaryTime
            ? html`<div class="dur">${secondaryTime}</div>`
            : nothing
        }
      </div>
      ${
        noThumbnail
          ? nothing
          : html`<div class="thumb">
              ${
                thumbnail
                  ? html`<img
                      src=${thumbnail}
                      alt=${
                        options.thumbnail === "class"
                          ? occurrence.name
                          : occurrence.instructor
                      }
                    />`
                  : initials
              }
            </div>`
      }
      <div>
        <div
          class=${className}
          role=${hasDescription ? "button" : nothing}
          tabindex=${hasDescription ? "0" : nothing}
          aria-expanded=${hasDescription ? String(model.expanded) : nothing}
          @click=${model.onToggleDescription}
          @keydown=${(event: KeyboardEvent) => {
            if (!hasDescription) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              model.onToggleDescription();
            }
          }}
        >
          <span>${occurrence.name}</span>${renderOfferingBadge(occurrence)}${
            occurrence.hasSubstitute
              ? html`<span class="sub-badge">Sub</span>`
              : nothing
          }${
            hasDescription
              ? html`<span class="exp-icon" aria-hidden="true"
                  >${ICON_CARET}</span
                >`
              : nothing
          }
        </div>
        ${
          options.hideInstructor && options.hideLocation
            ? nothing
            : html`<div class="cmeta">
                ${
                  options.hideInstructor
                    ? nothing
                    : hasBio
                      ? html`<span
                          class="ibio-btn expandable ${
                            model.bioExpanded ? "expanded" : ""
                          }"
                          role="button"
                          tabindex="0"
                          aria-expanded=${String(model.bioExpanded)}
                          aria-label=${`Instructor bio: ${occurrence.instructor}`}
                          @click=${(event: Event) => {
                            event.stopPropagation();
                            model.onToggleBio();
                          }}
                          @keydown=${(event: KeyboardEvent) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              model.onToggleBio();
                            }
                          }}
                          ><span class="ibio-name"
                            >${occurrence.instructor}</span
                          ><span class="exp-icon">${ICON_CARET}</span></span
                        >`
                      : html`<span>${occurrence.instructor}</span>`
                }
                ${
                  !options.hideInstructor &&
                  !options.hideLocation &&
                  occurrence.location
                    ? html`<span class="dot"> · </span>`
                    : ""
                }
                ${
                  options.hideLocation || !occurrence.location
                    ? nothing
                    : html`<a
                        class="loc"
                        href=${mapsHref(preferredLocationQuery(occurrence))}
                        target="_blank"
                        rel="noopener noreferrer"
                        @click=${(event: Event) => event.stopPropagation()}
                        >${occurrence.location}</a
                      >`
                }
              </div>`
        }
      </div>
      <div class="right ${occurrence.cancelled ? "is-cancelled" : ""}">
        ${
          occurrence.cancelled
            ? html`<span class="cancelled-note">Cancelled</span>`
            : external || model.isPast || model.registrationClosed || !model.capacity
              ? nothing
              : html`<span
                  class="cap ${model.capacity.waiting ? "waiting" : ""}"
                >
                  ${model.capacity.text}
                </span>`
        }
        ${
          occurrence.cancelled || model.isPast || (!external && model.registrationClosed)
            ? occurrence.cancelled
              ? nothing
              : html`<span class="cancelled-note"
                  >${model.isPast ? "Ended" : "Registration closed"}</span
                >`
            : external
              ? html`<a
                  class="book"
                  href=${occurrence.externalTicketUrl!}
                  target="_self"
                  aria-label=${label}
                ><span class="book-label-full" aria-hidden="true">${label}</span
                  ><span class="book-label-short" aria-hidden="true">${shortLabel}</span
                ></a>`
            : html`${
                  occurrence.offeringKind !== "program" &&
                  !notYetOpen && occurrence.buyoutAvailable &&
                  typeof occurrence.buyoutPriceMinor === "number"
                    ? html`<button
                        class="book buyout"
                        aria-label=${`Buy out this session for ${formatMinorAmount(
                          occurrence.buyoutPriceMinor,
                          occurrence.buyoutCurrency,
                        )}`}
                        @click=${() => model.onBuyout?.()}
                      >
                        Buy out —
                        ${formatMinorAmount(
                          occurrence.buyoutPriceMinor,
                          occurrence.buyoutCurrency,
                        )}
                      </button>`
                    : nothing
                }<button
                  class="book ${isWaitlist ? "waitlist" : ""}"
                  aria-label=${label}
                  ?disabled=${notYetOpen || (occurrence.offeringKind === "program" && !occurrence.programBookingUrl)}
                  title=${occurrence.offeringKind === "program" && !occurrence.programBookingUrl ? "Program registration unavailable" : nothing}
                  @click=${model.onBook}
                >
                  <span class="book-label-full" aria-hidden="true"
                    >${label}</span
                  ><span class="book-label-short" aria-hidden="true"
                    >${shortLabel}</span
                  >
                </button>`
        }
      </div>
      ${
        model.expanded && hasDescription
          ? html`<div class="cdesc">${
                model.descriptionText ||
                (model.descriptionLoading ? "Loading…" : nothing)
              }</div>`
          : nothing
      }
      ${
        !options.hideInstructor && model.bioExpanded && hasBio
          ? html`<div class="cbio">
              ${unsafeHTML(occurrence.instructorBio ?? "")}
            </div>`
          : nothing
      }
    </div>
  `;
}
